import React from "react";
import { Loader2, Save } from "lucide-react";
import { apiGetPengaturan, apiUpdatePengaturan, apiFileMasterRingkasan, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { NAMA_JENIS, labelStatus, ukuranTeks } from "@/lib/fileMaster";
import { ANGKA_LANJUTAN, ANGKA_UTAMA, SEMUA_JENIS, susunKirim, teksPenyimpanan } from "@/lib/fileMasterAtur";
import { rupiah } from "@/lib/format";

const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const inputCls = "mt-1 w-24 rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm text-slate-100 outline-none focus:border-amber-500/60";

// v2.22 (owner 2026-10-29, docs/rencana/FILE-MASTER-R2.md bagian 7.2): the "File master" card in Pengaturan.
// v2.22.1: an Owner sees storage status, GB stored and the everyday limits; the Administrator also gets
// "Pengaturan lanjutan" (closed by default): bucket limits, upload tuning, link lifetimes, price and cost.
export default function FileMasterPanel() {
  const { user } = useAuth();
  const adm = Boolean(user?.administrator);
  const [f, setF] = React.useState(null);
  const [r, setR] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const muat = React.useCallback(() => {
    apiGetPengaturan().then((p) => setF(Object.fromEntries(Object.entries(p).filter(([k]) => k.startsWith("file_master_"))))).catch((e) => setMsg({ ok: false, text: errText(e) }));
    apiFileMasterRingkasan().then(setR).catch(() => {});
  }, []);
  React.useEffect(() => { muat(); }, [muat]);
  if (!f) return null;

  const simpan = async () => {
    setBusy(true); setMsg(null);
    try {
      await apiUpdatePengaturan(susunKirim(f, adm));
      setMsg({ ok: true, text: "Pengaturan file master disimpan. Masa simpan baru berlaku untuk file berikutnya." });
      muat();
    } catch (e) { setMsg({ ok: false, text: errText(e) }); } finally { setBusy(false); }
  };
  const ubahJenis = (j, on) => setF({ ...f, file_master_jenis: on ? [...f.file_master_jenis, j] : f.file_master_jenis.filter((x) => x !== j) });
  const isian = ([k, label, unit, min, max]) => (
    <label key={k} className="flex flex-wrap items-center gap-2 text-xs text-slate-400">{label}
      <input data-testid={`fm-${k}`} type="number" min={min} max={max} value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className={inputCls} /> {unit}</label>
  );

  return (
    <div data-testid="fm-panel" className="rounded-xl border border-slate-800 bg-slate-900/60 p-5 space-y-3">
      <h2 className="text-[15px] font-bold text-soq-biru-tua">File master desain</h2>
      {r && (
        <div data-testid="fm-ringkasan" className="rounded-lg border border-slate-800 p-3 text-xs text-slate-300 space-y-1">
          <div>Penyimpanan: <b data-testid="fm-aktif">{teksPenyimpanan(r.aktif, adm)}</b></div>
          <div>Tersimpan sekarang: <b data-testid="fm-gb">{r.tersimpan_gb.toLocaleString("id-ID")} GB</b></div>
        </div>
      )}
      <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">{ANGKA_UTAMA.map(isian)}</div>
      <div className="text-xs text-slate-400">Jenis file yang boleh:
        <span className="ml-2 inline-flex flex-wrap gap-3">{SEMUA_JENIS.map((j) => (
          <label key={j} className="inline-flex items-center gap-1"><input type="checkbox" data-testid={`fm-jenis-${j}`} checked={f.file_master_jenis.includes(j)} onChange={(e) => ubahJenis(j, e.target.checked)} /> {NAMA_JENIS[j]}</label>))}</span>
      </div>
      <p className="text-xs text-slate-500">
        Aplikasi menghapus file otomatis setelah masa simpan (dihitung sejak file menjadi draf atau final). Angka baru hanya berlaku untuk file berikutnya.</p>
      {adm && (
        <details data-testid="fm-lanjutan" className="rounded-lg border border-slate-800 p-3">
          <summary className="cursor-pointer text-xs font-semibold text-slate-300">Pengaturan lanjutan (Administrator)</summary>
          <div className="mt-3 space-y-3">
            {r && (
              <div data-testid="fm-ringkasan-lanjutan" className="text-xs text-slate-300 space-y-1">
                <div>Perkiraan biaya bulan ini <b>${r.perkiraan_biaya_bulan_usd.toLocaleString("id-ID")}</b> (±{rupiah(r.perkiraan_biaya_bulan_rp)}; 10 GB pertama gratis)</div>
                <div className="flex flex-wrap gap-x-3">{Object.entries(r.per_status).map(([s, v]) => (
                  <span key={s}>{labelStatus(s)[0]}: {v.jumlah} file{["DRAF", "FINAL"].includes(s) ? ` (${ukuranTeks(v.byte)})` : ""}</span>))}</div>
                <div>Rata-rata unggah 100 MB (30 hari): <b>{r.rata_detik_per_100mb != null ? `${r.rata_detik_per_100mb} detik` : "belum ada data"}</b> dari {r.unggahan_30_hari} unggahan</div>
              </div>
            )}
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {ANGKA_LANJUTAN.map(isian)}
              <label className="flex flex-wrap items-center gap-2 text-xs text-slate-400">Harga per GB per bulan (USD)
                <input data-testid="fm-harga" value={f.file_master_harga_gb_usd ?? ""} onChange={(e) => setF({ ...f, file_master_harga_gb_usd: e.target.value })} className={inputCls} /></label>
              <label className="flex flex-wrap items-center gap-2 text-xs text-slate-400">Kurs (Rp per USD)
                <input data-testid="fm-kurs" type="number" value={f.file_master_kurs ?? ""} onChange={(e) => setF({ ...f, file_master_kurs: e.target.value })} className={inputCls} /></label>
            </div>
            <p className="text-xs text-slate-500">
              "Batas atas bucket" harus sama dengan aturan hapus di dasbor Cloudflare (draf 45, final 380 hari): masa simpan tidak boleh lebih panjang dari batas ini.
              Untuk memperpanjang, mis. final 2 tahun: (1) ubah aturan <code>hapus-final</code> di Cloudflare, (2) isi batas atas yang baru di sini, (3) baru ubah masa simpan.</p>
          </div>
        </details>
      )}
      <div className="flex items-center gap-3">
        <button data-testid="fm-simpan" type="button" disabled={busy || f.file_master_jenis.length === 0} onClick={simpan}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Simpan file master</button>
        {msg && <span data-testid="fm-panel-pesan" className={`text-xs ${msg.ok ? "text-emerald-300" : "text-red-300"}`}>{msg.text}</span>}
      </div>
    </div>
  );
}
